"use client";

import { useEffect, useRef } from "react";

export type NucleusState =
  | "idle"
  | "listening"
  | "processing"
  | "speaking"
  | "offline";

interface RadarProps {
  state: NucleusState;
  micLevel?: number;
  audioLevel?: number;
}

const TAU = Math.PI * 2;

// Icosahedron: 12 vertices, 30 edges. φ = golden ratio.
const PHI = (1 + Math.sqrt(5)) / 2;
const NORM = Math.hypot(1, PHI);

const VERTS: Array<[number, number, number]> = [
  [0, 1, PHI], [0, -1, PHI], [0, 1, -PHI], [0, -1, -PHI],
  [1, PHI, 0], [-1, PHI, 0], [1, -PHI, 0], [-1, -PHI, 0],
  [PHI, 0, 1], [PHI, 0, -1], [-PHI, 0, 1], [-PHI, 0, -1],
].map(([x, y, z]) => [x / NORM, y / NORM, z / NORM]);

const EDGES: Array<[number, number]> = [
  [0, 1], [0, 4], [0, 5], [0, 8], [0, 10],
  [1, 6], [1, 7], [1, 9], [1, 11],
  [2, 3], [2, 4], [2, 5], [2, 8], [2, 10],
  [3, 6], [3, 7], [3, 9], [3, 11],
  [4, 5], [4, 8], [4, 9],
  [5, 10], [5, 11],
  [6, 7], [6, 8], [6, 9],
  [7, 10], [7, 11],
  [8, 9], [10, 11],
];

function readColor(cssVar: string, fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const v = getComputedStyle(document.documentElement)
    .getPropertyValue(cssVar)
    .trim();
  return v || fallback;
}

interface Projected {
  x: number;
  y: number;
  z: number;
}

interface Spark {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number; // 1 = fresh, 0 = dead
  maxLife: number;
  color: string;
  size: number;
}

function project(
  p: [number, number, number],
  rotX: number,
  rotY: number,
  scale: number,
  cx: number,
  cy: number,
): Projected {
  let [x, y, z] = p;
  const cosY = Math.cos(rotY);
  const sinY = Math.sin(rotY);
  const x1 = x * cosY + z * sinY;
  const z1 = -x * sinY + z * cosY;
  const cosX = Math.cos(rotX);
  const sinX = Math.sin(rotX);
  const y1 = y * cosX - z1 * sinX;
  const z2 = y * sinX + z1 * cosX;
  return { x: cx + x1 * scale, y: cy - y1 * scale, z: z2 };
}

export function Radar({ state, micLevel = 0, audioLevel = 0 }: RadarProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef<NucleusState>(state);
  const micRef = useRef(micLevel);
  const audioRef = useRef(audioLevel);

  stateRef.current = state;
  micRef.current = micLevel;
  audioRef.current = audioLevel;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", { alpha: false });
    if (!ctx) return;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    let dpr = 1;
    let width = 0;
    let height = 0;
    let cx = 0;
    let cy = 0;
    let maxR = 0;

    const colors = {
      line: readColor("--color-line", "rgba(0, 229, 255, 0.18)"),
      lineStrong: readColor("--color-line-strong", "rgba(0, 229, 255, 0.45)"),
      cyan: readColor("--color-cyan", "#00E5FF"),
      cyanBright: readColor("--color-cyan-bright", "#5BFFEC"),
      cyanDim: readColor("--color-cyan-dim", "#007A8A"),
      bg: readColor("--color-bg", "#01060A"),
    };

    const beardImage = new Image();
    beardImage.src = "/hologram-beard.png";

    let rotY = 0.6;
    let rotX = 0.3;
    let pulse = 0;
    let lastT = performance.now();
    let raf = 0;
    let sparkAccumulator = 0;

    const SPARK_COLORS = ["#FFD040", "#FF8A20", "#FFB020", "#FFE070"];
    const SPARK_POOL_SIZE = 40;
    const sparks: Spark[] = [];

    function spawnSpark(extraSpeed: number) {
      const angle = Math.random() * TAU;
      const speed = (0.4 + Math.random() * 1.6) * (1 + extraSpeed);
      sparks.push({
        x: (Math.random() - 0.5) * 6,
        y: (Math.random() - 0.5) * 6,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life: 1,
        maxLife: 0.6 + Math.random() * 0.7,
        color: SPARK_COLORS[Math.floor(Math.random() * SPARK_COLORS.length)],
        size: 1 + Math.random() * 2,
      });
    }

    const init = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const rect = canvas.getBoundingClientRect();
      width = rect.width;
      height = rect.height;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cx = width / 2;
      cy = height / 2;
      maxR = Math.min(width, height) * 0.42;
    };

    init();
    window.addEventListener("resize", init);

    const render = (now: number) => {
      const dt = Math.min(0.05, (now - lastT) / 1000);
      lastT = now;
      const current = stateRef.current;
      const mic = current === "listening" ? micRef.current : 0;
      const rawAudio = current === "speaking" ? audioRef.current : 0;
      // La señal RMS suele ser discreta; la amplificamos solo en speaking
      // para que la luz responda claramente a cada palabra y respiración.
      const audio = current === "speaking"
        ? Math.min(1, rawAudio * 2.2 + 0.08)
        : 0;
      const activity = Math.max(mic, audio);

      const rotSpeed =
        current === "offline"
          ? -0.12
          : 0.35 + activity * 1.6;
      rotY += dt * rotSpeed;
      rotX = 0.25 + Math.sin(now / 2200) * 0.18;
      pulse += dt;

      // Background fill.
      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, width, height);

      // ====== Imagen holográfica completa ======
      // Conserva la imagen original completa y su canal alfa; se dibuja
      // como una capa grande y translúcida detrás del radar.
      if (beardImage.complete && beardImage.naturalWidth > 0) {
        const imageSize = maxR * 1.72;
        ctx.save();
        ctx.globalCompositeOperation = "screen";
        ctx.globalAlpha = 0.12 + activity * 0.12;
        ctx.drawImage(
          beardImage,
          cx - imageSize / 2,
          cy - imageSize / 2,
          imageSize,
          imageSize,
        );
        ctx.restore();
      }

      // ====== Outer halo glow ======
      const haloR = maxR * 1.15;
      const haloGrad = ctx.createRadialGradient(cx, cy, maxR * 0.55, cx, cy, haloR);
      const pulseAlpha = 0.18 + 0.07 * Math.sin(pulse * 2.2) + activity * 0.28;
      haloGrad.addColorStop(0, hexToRgba(colors.cyan, pulseAlpha));
      haloGrad.addColorStop(0.6, hexToRgba(colors.cyan, pulseAlpha * 0.3));
      haloGrad.addColorStop(1, hexToRgba(colors.cyan, 0));
      ctx.fillStyle = haloGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, haloR, 0, TAU);
      ctx.fill();

      // ====== Anillo exterior grueso (con glow) ======
      ctx.lineWidth = 5 + activity * 5;
      ctx.strokeStyle = hexToRgba(colors.cyan, 0.18 + activity * 0.25);
      ctx.beginPath();
      ctx.arc(cx, cy, maxR, 0, TAU);
      ctx.stroke();
      ctx.lineWidth = 2.2 + activity * 2.2;
      ctx.strokeStyle = hexToRgba(colors.cyanBright, 0.7 + activity * 0.3);
      ctx.beginPath();
      ctx.arc(cx, cy, maxR, 0, TAU);
      ctx.stroke();

      // ====== Banda de datos (anillo fino + segmento de dashes dorados + anillo fino) ======
      // Un anillo un poco más adentro con dashes estilo "data readout".
      ctx.lineWidth = 1;
      ctx.strokeStyle = colors.lineStrong;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR * 0.88, 0, TAU);
      ctx.stroke();
      // Dashes color cyan-bright entre los dos anillos finos.
      ctx.setLineDash([4, 6]);
      ctx.strokeStyle = colors.cyanBright;
      ctx.lineDashOffset = -pulse * 30;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR * 0.85, 0, TAU);
      ctx.stroke();
      ctx.setLineDash([]);
      // Anillo fino interior a la banda.
      ctx.lineWidth = 1;
      ctx.strokeStyle = colors.lineStrong;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR * 0.78, 0, TAU);
      ctx.stroke();

      // ====== Anillo interno con ticks blancos cortos (40 ticks) ======
      ctx.strokeStyle = colors.lineStrong;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(cx, cy, maxR * 0.55, 0, TAU);
      ctx.stroke();
      ctx.strokeStyle = hexToRgba("#FFFFFF", 0.6);
      ctx.lineWidth = 1.4;
      const ticks = 40;
      for (let i = 0; i < ticks; i++) {
        const a = (i / ticks) * TAU - Math.PI / 2;
        const inner = maxR * 0.58;
        const outer = maxR * 0.62;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
        ctx.lineTo(cx + Math.cos(a) * outer, cy + Math.sin(a) * outer);
        ctx.stroke();
      }

      // ====== Anillo más interno (contorno del "atom cage") ======
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = hexToRgba(colors.cyan, 0.85);
      ctx.beginPath();
      ctx.arc(cx, cy, maxR * 0.35, 0, TAU);
      ctx.stroke();

      // ========================================================
      // ====== Esfera wireframe viva (icosaedro rotando) =======
      // ========================================================
      const sphereScale = maxR * 0.22 * (1 + activity * 0.18);

      const projected: Projected[] = VERTS.map((v) =>
        project(v, rotX, rotY, sphereScale, cx, cy)
      );

      // Halo suave detrás de la esfera.
      const sphereHalo = ctx.createRadialGradient(cx, cy, 0, cx, cy, sphereScale * 2.1);
      const sphereColor =
        current === "offline" ? colors.cyanDim : colors.cyan;
      sphereHalo.addColorStop(0, hexToRgba(sphereColor, 0.25 + activity * 0.25));
      sphereHalo.addColorStop(0.6, hexToRgba(sphereColor, 0.08));
      sphereHalo.addColorStop(1, hexToRgba(sphereColor, 0));
      ctx.fillStyle = sphereHalo;
      ctx.beginPath();
      ctx.arc(cx, cy, sphereScale * 2.1, 0, TAU);
      ctx.fill();

      // Aristas. Las más cercanas al observador (z alto) más brillantes.
      // Ordenar para que las traseras se dibujen primero.
      const edgesWithDepth = EDGES.map(([a, b]) => ({
        a,
        b,
        depth: (projected[a].z + projected[b].z) / 2,
      })).sort((e1, e2) => e1.depth - e2.depth);

      ctx.lineWidth = 1.2;
      ctx.lineCap = "round";
      for (const e of edgesWithDepth) {
        const pa = projected[e.a];
        const pb = projected[e.b];
        // Mapear z (en [-1, 1]) a alpha: z=1 (frente) → 1, z=-1 (atrás) → 0.35
        const a = 0.35 + 0.65 * Math.max(0, Math.min(1, (e.depth + 1) / 2));
        ctx.strokeStyle = hexToRgba(colors.cyanBright, a);
        ctx.beginPath();
        ctx.moveTo(pa.x, pa.y);
        ctx.lineTo(pb.x, pb.y);
        ctx.stroke();
      }

      // Pequeños dots en cada vértice.
      ctx.globalCompositeOperation = "lighter";
      for (const p of projected) {
        const a = 0.5 + 0.5 * Math.max(0, Math.min(1, (p.z + 1) / 2));
        ctx.fillStyle = hexToRgba(colors.cyanBright, a);
        ctx.beginPath();
        ctx.arc(p.x, p.y, 1.5, 0, TAU);
        ctx.fill();
      }
      ctx.globalCompositeOperation = "source-over";

      // ========================================================
      // ====== Chispas amarillo/naranja en todas direcciones =====
      // ========================================================
      // Spawn rate sube con actividad; más chispas cuando hay audio/mic.
      const targetPool = Math.floor(SPARK_POOL_SIZE * (0.4 + activity * 0.9));
      sparkAccumulator += dt * (4 + activity * 30);
      while (sparks.length < targetPool && sparkAccumulator > 0) {
        spawnSpark(activity);
        sparkAccumulator -= 1;
      }
      if (sparkAccumulator > 4) sparkAccumulator = 4;

      // Mover y envejecer. Las chispas se desaceleran con drag y se desvanecen
      // cuando salen del scope (|pos| > maxR * 0.6).
      const sparkBounds = maxR * 0.62;
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.x += s.vx * dt * maxR * 0.35;
        s.y += s.vy * dt * maxR * 0.35;
        s.vx *= Math.pow(0.45, dt);
        s.vy *= Math.pow(0.45, dt);
        s.life -= dt / s.maxLife;
        const r = Math.hypot(s.x, s.y);
        if (s.life <= 0 || r > sparkBounds) {
          sparks.splice(i, 1);
        }
      }

      // Dibujar las chispas con glow (radial gradient) + estela de 3 dots.
      ctx.save();
      ctx.translate(cx, cy);
      for (const s of sparks) {
        const a = Math.max(0, Math.min(1, s.life));
        // Estela corta hacia atrás.
        for (let t = 1; t <= 3; t++) {
          const ta = t / 3;
          const tx = s.x - s.vx * ta * dt * maxR * 0.12;
          const ty = s.y - s.vy * ta * dt * maxR * 0.12;
          ctx.fillStyle = hexToRgba(s.color, 0.45 * a * (1 - ta));
          ctx.beginPath();
          ctx.arc(tx, ty, s.size * (1 - ta * 0.6), 0, TAU);
          ctx.fill();
        }
        // Glow.
        const g = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, 7);
        g.addColorStop(0, hexToRgba(s.color, a));
        g.addColorStop(0.4, hexToRgba(s.color, 0.5 * a));
        g.addColorStop(1, hexToRgba(s.color, 0));
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(s.x, s.y, 7, 0, TAU);
        ctx.fill();
        // Núcleo blanco-amarillo.
        ctx.fillStyle = hexToRgba("#FFFFFF", 0.95 * a);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.size, 0, TAU);
        ctx.fill();
      }
      ctx.restore();

      // ========================================================
      // ====== Electrones amarillo/naranja orbitando ============
      // ========================================================
      // 10 electrones en órbitas distintas — cubren todas las direcciones.
      const ELECTRONS = [
        { color: "#FFD040", orbitR: 0.34, tilt: 0,                  speed: 1.4,  trail: 7 },
        { color: "#FF8A20", orbitR: 0.40, tilt: Math.PI / 3,       speed: 1.0,  trail: 6 },
        { color: "#FFB020", orbitR: 0.36, tilt: -Math.PI / 3,      speed: -1.2, trail: 6 },
        { color: "#FFE070", orbitR: 0.46, tilt: Math.PI / 6,       speed: 0.8,  trail: 5 },
        { color: "#FF8A20", orbitR: 0.30, tilt: -Math.PI / 6,      speed: -1.6, trail: 6 },
        { color: "#FFD040", orbitR: 0.44, tilt: Math.PI / 2,       speed: 0.7,  trail: 5 },
        { color: "#FFB020", orbitR: 0.32, tilt: -Math.PI / 2,      speed: -0.9, trail: 6 },
        { color: "#FFE070", orbitR: 0.48, tilt: (2 * Math.PI) / 3, speed: 1.1,  trail: 5 },
        { color: "#FFD040", orbitR: 0.38, tilt: -(2 * Math.PI) / 3, speed: -1.3, trail: 6 },
        { color: "#FF8A20", orbitR: 0.42, tilt: Math.PI,           speed: 1.2,  trail: 5 },
      ];
      const electronSpeedMul =
        current === "offline" ? 0.5 : 1 + activity * 2.4;
      ctx.save();
      ctx.translate(cx, cy);
      for (const e of ELECTRONS) {
        ctx.save();
        ctx.rotate(e.tilt);
        const r = maxR * e.orbitR;
        // Órbita muy tenue.
        ctx.strokeStyle = hexToRgba(e.color, 0.18);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, TAU);
        ctx.stroke();
        // Posición del electrón.
        const a = pulse * e.speed * electronSpeedMul;
        const ex = Math.cos(a) * r;
        const ey = Math.sin(a) * r;
        // Estela de puntos detrás del electrón.
        for (let t = 1; t <= e.trail; t++) {
          const ta = a - t * 0.08 * Math.sign(e.speed);
          const tx = Math.cos(ta) * r;
          const ty = Math.sin(ta) * r;
          const falloff = 1 - t / (e.trail + 1);
          ctx.fillStyle = hexToRgba(e.color, 0.55 * falloff);
          ctx.beginPath();
          ctx.arc(tx, ty, 2.0 * falloff + 0.6, 0, TAU);
          ctx.fill();
        }
        // Glow del electrón (gradiente radial).
        const eg = ctx.createRadialGradient(ex, ey, 0, ex, ey, 9);
        eg.addColorStop(0, hexToRgba(e.color, 1));
        eg.addColorStop(0.3, hexToRgba(e.color, 0.7));
        eg.addColorStop(1, hexToRgba(e.color, 0));
        ctx.fillStyle = eg;
        ctx.beginPath();
        ctx.arc(ex, ey, 9, 0, TAU);
        ctx.fill();
        // Núcleo blanco-amarillo del electrón.
        ctx.fillStyle = hexToRgba("#FFFFFF", 0.95);
        ctx.beginPath();
        ctx.arc(ex, ey, 1.8, 0, TAU);
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();

      // ====== Núcleo blanco en el centro ======
      const coreR = maxR * 0.018 * (1 + activity * 0.5);
      const coreGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR * 6);
      coreGrad.addColorStop(0, "rgba(255, 255, 255, 1)");
      coreGrad.addColorStop(0.2, hexToRgba(colors.cyanBright, 0.85));
      coreGrad.addColorStop(0.6, hexToRgba(colors.cyan, 0.3));
      coreGrad.addColorStop(1, hexToRgba(colors.cyan, 0));
      ctx.fillStyle = coreGrad;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR * 6, 0, TAU);
      ctx.fill();
      ctx.fillStyle = "rgba(255, 255, 255, 1)";
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, TAU);
      ctx.fill();

      raf = requestAnimationFrame(render);
    };

    if (!reducedMotion) {
      raf = requestAnimationFrame(render);
    } else {
      render(performance.now());
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", init);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      aria-hidden="true"
    />
  );
}

// Convert "#RRGGBB" / "rgba(...)" to "rgba(r,g,b,a)".
function hexToRgba(input: string, alpha: number): string {
  if (input.startsWith("rgba")) {
    return input.replace(
      /rgba\(([^)]+)\)/,
      (_m, parts) => {
        const [r, g, b] = parts.split(",").map((s: string) => s.trim());
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    );
  }
  if (input.startsWith("rgb(")) {
    const m = input.match(/rgb\(([^)]+)\)/);
    if (m) {
      const [r, g, b] = m[1].split(",").map((s: string) => s.trim());
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  if (input.startsWith("#")) {
    let h = input.slice(1);
    if (h.length === 3) h = h.split("").map((c) => c + c).join("");
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return input;
}
